import React, { useMemo, useState } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";
import ShipeeziNewGrid, { Column } from "Components/AppGrid/shipeezi-new-grid";
import { CircularProgress } from "@mui/material";
import { startOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";

import Button, { Color } from "Components/Form/Button";
import InputDateRange from "Components/Form/Input/DateRange";
import { NotificationToast } from "Components/Generic/Toast/NotificationToast";
import { useAppState } from "Context/StateContext";
import { OrdersManagement } from "Models/report.orders.management";
import { Searches } from "Models/shipeezi.table";
import OrdersManagementReportService from "Services/orders.management.report.service";
import { jsonToCsv } from "Util/generateCsv";
import { getOrderReportDateRange, mapOrderReportExportRows } from "./orderReportUtils";

const getDefaultDateRange = () => ({
  startDate: startOfMonth(new Date()),
  endDate: new Date(),
});

const downloadCsv = (filename: string, data: Array<Record<string, string>>) => {
  const jsonString = `data:text/csv;charset=utf-8,${encodeURIComponent(jsonToCsv(data))}`;
  const link = document.createElement("a");

  link.href = jsonString;
  link.download = `${filename}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const OrderReportScreen = () => {
  const { t: translate } = useTranslation();
  const { currentUser } = useAppState();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [searches, setSearches] = useState<Searches>([]);
  const [startDate, setStartDate] = useState<Date>(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState<Date>(() => getDefaultDateRange().endDate);

  const dateRange = useMemo(
    () => getOrderReportDateRange(startDate, endDate),
    [endDate, startDate]
  );

  const reportFilters = useMemo(
    () => ({
      companyId: JSON.stringify([String(currentUser?.companyId || "")]),
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      searchByOr: searches?.length ? JSON.stringify(searches) : "",
      page,
      pageSize,
    }),
    [currentUser?.companyId, dateRange, searches, page, pageSize]
  );
  const exportFilters = useMemo(
    () => ({
      companyId: reportFilters.companyId,
      startDate: reportFilters.startDate,
      endDate: reportFilters.endDate,
      searchByOr: reportFilters.searchByOr,
    }),
    [
      reportFilters.companyId,
      reportFilters.endDate,
      reportFilters.searchByOr,
      reportFilters.startDate,
    ]
  );

  const { data, isFetching } = useQuery(
    ["orders.management.reports.order-report", reportFilters],
    () => OrdersManagementReportService.getOrdersManagementReports(reportFilters),
    {
      enabled: !!currentUser?.companyId,
      refetchOnWindowFocus: false,
    }
  );

  const orders = data?.data?.orders || [];
  const total = data?.data?.count || 0;

  const { mutate: exportCsv, isLoading: isExporting } = useMutation(
    ["orders.management.reports.order-report.export", dateRange, currentUser?.companyId],
    () =>
      OrdersManagementReportService.getOrdersManagementOrderReportExportCSVData(
        exportFilters
      ),
    {
      onSuccess: (response) => {
        const mapped = mapOrderReportExportRows(response?.data || []);

        downloadCsv(`order_report_${dateRange.startDate}_${dateRange.endDate}`, mapped);
      },
      onError: () => {
        NotificationToast.error(translate("anErrorOccurredTryAgain"));
      },
    }
  );

  const columns: Column[] = [
    {
      name: "reference",
      title: translate("reference"),
    },
    {
      name: "mode",
      title: translate("mode"),
      formatComponent: (mode: string, row?: OrdersManagement) =>
        mode || row?.type || "",
      hideSearch: true,
    },
    {
      name: "customer",
      title: translate("customer"),
      formatText: (customer: OrdersManagement["customer"]) => customer?.fullName || "",
    },
    {
      name: "branchFrom",
      title: translate("origin"),
      formatText: (branchFrom: OrdersManagement["branchFrom"]) => branchFrom?.name || "",
      hideSearch: true,
    },
    {
      name: "toBranch",
      title: translate("destination"),
      formatText: (toBranch: OrdersManagement["toBranch"]) => toBranch?.name || "",
      hideSearch: true,
    },
  ];

  const handleCurrentPage = (numPage: number) => {
    setPage(numPage - 1);
  };

  const handlePageSize = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const renderDateRangeSelector = () => (
    <InputDateRange
      key={`${dateRange.startDate}-${dateRange.endDate}`}
      pStartDate={startDate}
      pEndDate={endDate}
      onChange={(range) => {
        if (!range?.startDate || !range?.endDate) {
          const defaultRange = getDefaultDateRange();
          setStartDate(defaultRange.startDate);
          setEndDate(defaultRange.endDate);
          setPage(0);
          return;
        }

        setStartDate(range.startDate);
        setEndDate(range.endDate);
        setPage(0);
      }}
      data-cy="order-report-date-range"
    />
  );

  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <ShipeeziNewGrid
        loadingData={isFetching}
        data={orders}
        totalItems={total}
        columns={columns}
        currentPage={page}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        onNextPage={handleCurrentPage}
        onPreviousPage={handleCurrentPage}
        onGoToPage={handleCurrentPage}
        onChangePageSize={handlePageSize}
        generalActions={{
          orderBy: {
            left: ["dateRangeSelector", "handleSearch"],
            right: ["exportCSV"],
          },
          dateRangeSelector: renderDateRangeSelector(),
          handleSearch: {
            onSearch: (values: Searches) => {
              setSearches(values);
              setPage(0);
            },
          },
          exportCSV: (
            <Button
              onClick={() => exportCsv()}
              customColor={Color.SECONDARY}
              disabled={isExporting}
            >
              {isExporting ? <CircularProgress size={14} /> : translate("exportCSV")}
            </Button>
          ),
        }}
      />
    </div>
  );
};
